import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import utils from 'src/utils/utils';

interface UseTiltActionsProps {
  cardRef: RefObject<HTMLDivElement | null>;
  setIsInteracting: (value: boolean) => void;
  isEnabled: boolean;
}

/**
 * 카드 틸트(기울기) 효과를 제어하는 훅
 *
 * @description
 * 마우스/터치 인터랙션에 반응하여 카드의 3D 틸트 효과를 처리합니다.
 *
 * @param props
 * ```ts
 * {
 *   cardRef: RefObject<HTMLDivElement>,  // 틸트 효과를 적용할 카드 요소
 *   setIsInteracting: (state: boolean) => void,  // 인터랙션 상태 변경 함수
 *   isEnabled: boolean                   // 틸트 기능 활성화 여부
 * }
 * ```
 *
 * @returns
 * ```ts
 * {
 *   handlers: {
 *     onMouseEnter: (e: React.MouseEvent | React.TouchEvent) => void,
 *     onMouseLeave: () => void,
 *     onMouseMove: (e: React.MouseEvent | React.TouchEvent) => void,
 *     onTouchStart: (e: React.TouchEvent) => void,
 *     onTouchMove: (e: React.TouchEvent) => void,
 *     onTouchEnd: () => void
 *   }                                    // 마우스/터치 이벤트 핸들러 모음
 * }
 * ```
 */
export const useTiltActions = ({ cardRef, setIsInteracting, isEnabled }: UseTiltActionsProps) => {
  const cachedRect = useRef<DOMRect | null>(null);
  const rafId = useRef<number>(0);

  /**
   * 마우스/터치 이벤트에서 요소 기준 상대 좌표를 추출
   */
  const getCoordinates = useCallback((e: React.MouseEvent | React.TouchEvent, rect: DOMRect) => {
    const { clientX, clientY } = 'touches' in e ? e.touches[0] : (e as React.MouseEvent);
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  }, []);

  /**
   * 좌표값을 기반으로 카드의 회전각도와 광원 효과 위치를 계산
   */
  const updateTiltEffect = useCallback((x: number, y: number, element: HTMLElement, rect: DOMRect, isEnabled: boolean) => {
    // 1. 마우스 위치를 0-100 사이의 퍼센트로 변환
    const percentX = Math.abs(Math.floor((100 / rect.width) * x) - 100);
    const percentY = Math.abs(Math.floor((100 / rect.height) * y) - 100);

    // 2. 틸트 효과를 부드럽게 하기 위한 보정값 계산
    const tiltPositionX = 50 + (percentX - 50) / 1.5;
    const tiltPositionY = 50 + (percentY - 50) / 1.5;

    // 3. 회전 각도와 광원 효과 계산
    const rotateX = ((tiltPositionY - 50) / 2) * -1;
    const rotateY = ((tiltPositionX - 50) / 1.5) * 0.5;

    // 4. CSS 변수 설정
    element.style.setProperty('--rotate-x', `${isEnabled ? rotateX : 0}deg`);
    element.style.setProperty('--rotate-y', `${isEnabled ? rotateY : 0}deg`);

    element.style.setProperty('--card-width', `${rect.width}px`);
    element.style.setProperty('--light-x', `${(x / rect.width) * 100}%`);
    element.style.setProperty('--light-y', `${(y / rect.height) * 100}%`);
    element.style.setProperty('--light-opacity', '1');
  }, []);

  /**
   * 인터랙션 시작 핸들러 (enter/touchstart)
   */
  const handleStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!cardRef.current) return;

      if (utils.isMobilePlatform() && !('touches' in e)) {
        e.preventDefault();
        return;
      }

      // rect를 시작 시점에 캐싱
      cachedRect.current = cardRef.current.getBoundingClientRect();
      setIsInteracting(true);

      const { x, y } = getCoordinates(e, cachedRect.current);
      updateTiltEffect(x, y, cardRef.current, cachedRect.current, isEnabled);
    },
    [isEnabled, cardRef, getCoordinates, updateTiltEffect, setIsInteracting],
  );

  /**
   * 마우스/터치 이동 핸들러 (rAF 쓰로틀링)
   */
  const handleMove = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!cardRef.current || !cachedRect.current) return;

      if (utils.isMobilePlatform() && !('touches' in e)) {
        e.preventDefault();
        return;
      }

      const rect = cachedRect.current;
      const { x, y } = getCoordinates(e, rect);

      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        if (!cardRef.current) return;
        updateTiltEffect(x, y, cardRef.current, rect, isEnabled);
      });
    },
    [isEnabled, cardRef, getCoordinates, updateTiltEffect],
  );

  /**
   * 마우스/터치 이벤트 종료 핸들러
   */
  useEffect(() => {
    return () => {
      cancelAnimationFrame(rafId.current);
    };
  }, []);

  const handleLeave = useCallback(() => {
    if (!cardRef.current) return;

    cancelAnimationFrame(rafId.current);
    cachedRect.current = null;

    cardRef.current.style.setProperty('--rotate-x', '0deg');
    cardRef.current.style.setProperty('--rotate-y', '0deg');
    cardRef.current.style.setProperty('--light-opacity', '0');

    setIsInteracting(false);
  }, [cardRef, setIsInteracting]);

  return {
    handlers: {
      onMouseEnter: handleStart,
      onMouseMove: handleMove,
      onMouseLeave: handleLeave,
      onTouchStart: handleStart,
      onTouchMove: handleMove,
      onTouchEnd: handleLeave,
    },
  };
};
